import { useCallback, useEffect, useState } from "react";
import type { HealthStatus } from "../types/api";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Filter by site key. `all` shows every site; otherwise only the matching
 * `siteKey` is rendered. Values match the contract's enum exactly.
 */
export type SiteFilter = "all" | "hoban" | "ansan";

/**
 * Filter by bridge health status. `all` shows every bridge inside each card.
 */
export type StatusFilter = "all" | HealthStatus;

/**
 * Sort order for bridges inside a site card.
 *
 *   - `triage`          : DOWN → UP(오래된 갱신 순) → UNKNOWN → STALE  ← default
 *                         (대응 우선순위 — 정체 먼저, 운영 종료는 맨 아래)
 *   - `name-asc`        : lotName 가나다 오름차순
 *   - `name-desc`       : lotName 가나다 내림차순
 *   - `updated-oldest`  : lastUpdatedAt asc (null이 가장 위)
 *   - `updated-newest`  : lastUpdatedAt desc (null이 가장 아래)
 *   - `status-down-first`: DOWN → UNKNOWN → UP → STALE
 */
export type BridgeSort =
  | "triage"
  | "name-asc"
  | "name-desc"
  | "updated-oldest"
  | "updated-newest"
  | "status-down-first";

export interface FilterState {
  site: SiteFilter;
  status: StatusFilter;
  sort: BridgeSort;
}

export const DEFAULT_FILTERS: FilterState = {
  site: "all",
  status: "all",
  sort: "triage",
};

// Bumped to v4 because the API response now nests floors[] inside each bridge.
// The filter shape itself is unchanged, but the version bump keeps prior caches
// from masking shape-driven UI regressions in older builds.
const STORAGE_KEY = "bridgechecker.filters.v4";

const VALID_SITE: ReadonlySet<SiteFilter> = new Set<SiteFilter>([
  "all",
  "hoban",
  "ansan",
]);
const VALID_STATUS: ReadonlySet<StatusFilter> = new Set<StatusFilter>([
  "all",
  "UP",
  "DOWN",
  "STALE",
  "RETIRED",
  "UNKNOWN",
]);
const VALID_SORT: ReadonlySet<BridgeSort> = new Set<BridgeSort>([
  "triage",
  "name-asc",
  "name-desc",
  "updated-oldest",
  "updated-newest",
  "status-down-first",
]);

// ─── Storage helpers ──────────────────────────────────────────────────────────

function readInitialFilters(): FilterState {
  if (typeof window === "undefined") return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    return {
      site:
        parsed.site && VALID_SITE.has(parsed.site)
          ? parsed.site
          : DEFAULT_FILTERS.site,
      status:
        parsed.status && VALID_STATUS.has(parsed.status)
          ? parsed.status
          : DEFAULT_FILTERS.status,
      sort:
        parsed.sort && VALID_SORT.has(parsed.sort)
          ? parsed.sort
          : DEFAULT_FILTERS.sort,
    };
  } catch {
    // localStorage 비활성/파싱 실패 → 안전한 기본값
    return DEFAULT_FILTERS;
  }
}

function persist(state: FilterState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota exceeded / disabled storage — silently ignore */
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseFiltersReturn {
  filters: FilterState;
  setSite: (v: SiteFilter) => void;
  setStatus: (v: StatusFilter) => void;
  setSort: (v: BridgeSort) => void;
  reset: () => void;
}

/**
 * Page-level filter/sort state with localStorage persistence.
 *
 * Stored under key `bridgechecker.filters.v1` (bumped if the schema changes).
 * Reads synchronously via `useState`'s lazy initializer so the first paint
 * already reflects the persisted choice.
 */
export function useFilters(): UseFiltersReturn {
  const [filters, setFilters] = useState<FilterState>(readInitialFilters);

  useEffect(() => {
    persist(filters);
  }, [filters]);

  const setSite = useCallback((v: SiteFilter) => {
    setFilters((prev) => ({ ...prev, site: v }));
  }, []);
  const setStatus = useCallback((v: StatusFilter) => {
    setFilters((prev) => ({ ...prev, status: v }));
  }, []);
  const setSort = useCallback((v: BridgeSort) => {
    setFilters((prev) => ({ ...prev, sort: v }));
  }, []);
  const reset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return { filters, setSite, setStatus, setSort, reset };
}
